<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDealsStore } from 'dashboard/stores/deals';
import { useActivitiesStore } from 'dashboard/stores/activities';
import { useDealNotesStore } from 'dashboard/stores/dealNotes';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ActivityList from 'dashboard/components-next/Activities/ActivityList.vue';
import DealHeader from 'dashboard/components-next/Deals/DealHeader.vue';
import DealNotes from 'dashboard/components-next/Deals/DealNotes.vue';
import DealHistory from 'dashboard/components-next/Deals/DealHistory.vue';

const { t } = useI18n();
const route = useRoute();
const dealsStore = useDealsStore();
const activitiesStore = useActivitiesStore();
const dealNotesStore = useDealNotesStore();

const dealId = computed(() => route.params.dealId);
const deal = computed(() => dealsStore.getActiveDeal);
const uiFlags = computed(() => dealsStore.uiFlags);
const activities = computed(() => activitiesStore.getActivitiesList);

const activeTab = ref('activities');

const handleCompleteActivity = async activityId => {
  try {
    await activitiesStore.complete(activityId);
    useAlert(t('ACTIVITIES.COMPLETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('ACTIVITIES.ERROR_MESSAGE'));
  }
};

const loadDeal = async () => {
  await dealsStore.show(dealId.value);
  await activitiesStore.get({ deal_id: dealId.value });
  await dealNotesStore.get(dealId.value);
};

watch(dealId, loadDeal, { immediate: false });

onMounted(loadDeal);
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-auto bg-n-surface-1">
    <Spinner v-if="uiFlags.fetchingItem" />
    <template v-else>
      <DealHeader :deal="deal" />

      <div
        class="sticky top-0 z-10 px-6 bg-n-surface-1 border-b border-n-slate-4 dark:border-n-slate-8"
      >
        <div class="flex gap-6 mx-auto max-w-5xl">
          <button
            class="py-3 text-sm font-medium border-b-2 transition-colors"
            :class="
              activeTab === 'activities'
                ? 'border-woot-500 text-woot-500'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            "
            @click="activeTab = 'activities'"
          >
            {{ t('DEALS.DETAIL.TABS.ACTIVITIES') }}
          </button>
          <button
            class="py-3 text-sm font-medium border-b-2 transition-colors"
            :class="
              activeTab === 'notes'
                ? 'border-woot-500 text-woot-500'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            "
            @click="activeTab = 'notes'"
          >
            {{ t('DEALS.DETAIL.TABS.NOTES') }}
          </button>
          <button
            class="py-3 text-sm font-medium border-b-2 transition-colors"
            :class="
              activeTab === 'history'
                ? 'border-woot-500 text-woot-500'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            "
            @click="activeTab = 'history'"
          >
            {{ t('DEALS.DETAIL.TABS.HISTORY') }}
          </button>
          <button
            class="py-3 text-sm font-medium border-b-2 transition-colors"
            :class="
              activeTab === 'info'
                ? 'border-woot-500 text-woot-500'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            "
            @click="activeTab = 'info'"
          >
            {{ t('DEALS.DETAIL.TABS.INFO') }}
          </button>
        </div>
      </div>

      <main class="flex-1 overflow-y-auto px-6">
        <div class="mx-auto max-w-5xl py-4">
          <ActivityList
            v-if="activeTab === 'activities'"
            :activities="activities"
            @complete="handleCompleteActivity"
          />
          <DealNotes v-else-if="activeTab === 'notes'" :deal-id="dealId" />
          <DealHistory
            v-else-if="activeTab === 'history'"
            :activities="activities"
            @complete="handleCompleteActivity"
          />
          <div v-else class="space-y-4">
            <dl class="grid grid-cols-2 gap-4">
              <div>
                <dt class="text-sm text-slate-500">
                  {{ t('DEALS.FIELDS.AMOUNT') }}
                </dt>
                <dd
                  class="text-base font-medium text-slate-900 dark:text-slate-100"
                >
                  {{ deal.amount || '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-sm text-slate-500">
                  {{ t('DEALS.FIELDS.EXPECTED_CLOSE_DATE') }}
                </dt>
                <dd
                  class="text-base font-medium text-slate-900 dark:text-slate-100"
                >
                  {{ deal.expectedCloseDate || '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-sm text-slate-500">
                  {{ t('DEALS.FIELDS.STATUS') }}
                </dt>
                <dd
                  class="text-base font-medium text-slate-900 dark:text-slate-100"
                >
                  {{
                    t(`DEALS.STATUS.${(deal.status || 'open').toUpperCase()}`)
                  }}
                </dd>
              </div>
              <div>
                <dt class="text-sm text-slate-500">
                  {{ t('DEALS.FIELDS.STAGE') }}
                </dt>
                <dd
                  class="text-base font-medium text-slate-900 dark:text-slate-100"
                >
                  {{ deal.stage?.name || '—' }}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </main>
    </template>
  </div>
</template>
